import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import {
  App,
  Gefahr,
  Leistung,
  Produkt,
  Produktlinie,
  TaasTarifrisikoschluessel,
  VwgVorbelegungdaten,
  waehlbareGefahren,
} from '@ps20-frontend/frontend-models';
import { produktReducer, updateZusatzoptionen } from '@ps20-frontend/reducers';
import {
  Produktlinienname,
  TaasTarifrisikoschluesselVWGV2,
  VorschadenArt,
  VorvertragBaustein,
} from '@ps20/bff-models';
import { GlobalActions, MasterProduktData, ZUSATZOPTION_CHECKED } from '@ps20-frontend/shop-integration-redux';
import { GefahrChecked } from '../action-types/gefahrChecked';
import { IAppState } from '../appState';
import { VwgActions } from '../vwgActions';
import { ZusatzoptionenNameVWGEnum } from '../models/zusatzoptionenVWG.model';

export const vwgV2ProduktReducer = function (
  sectionState: Produkt = null,
  action: AnyAction,
  state: IAppState,
): Produkt {
  let newSectionState = produktReducer(sectionState, action, App.VWG_V2, state?.angebotsId);
  newSectionState = applyOeltankDisableLogic(newSectionState, state);

  switch (action.type) {
    case VwgActions.GEFAHREN_CHECKED: {
      const actionPayload: Array<GefahrChecked> = action.payload;
      const newState: Produkt = cloneDeep(newSectionState);

      for (const gefahrFromAction of actionPayload) {
        const produktlinie: Produktlinie = newState.produktlinien.find(
          elem => elem.produktName === gefahrFromAction.tarifItem.produktName,
        );

        const gefahr: Gefahr = produktlinie.gefahren.find(elem => elem.id === gefahrFromAction.id);

        gefahr.isSelected = gefahrFromAction.isSelected;
        if (waehlbareGefahren.includes(gefahr.id)) {
          changeLeistungenBeiGefahr(produktlinie.leistungen, gefahr);
        }

        produktlinie.leistungen.find(elem => elem.id === 'versicherteGefahren').eigenschaft =
          getVersicherteGefahrenCommaSeperated(produktlinie);
      }

      return newState;
    }
    case VwgActions.BRUTTOPREISE_CALCULATED:
    case VwgActions.PRODUKTLINIEN_CHANGED: {
      const produktlinien: Produktlinie[] = action.payload;
      return { ...newSectionState, produktlinien };
    }
    case VwgActions.OELTANK_VORBELEGEN: {
      const updatedProduktlinien: Produktlinie[] = [];
      newSectionState.produktlinien.forEach(
        (produktlinie, index) =>
          (updatedProduktlinien[index] = updateZusatzoptionen(
            produktlinie,
            TaasTarifrisikoschluesselVWGV2.OEL_TANK,
            true,
          )),
      );
      return { ...newSectionState, produktlinien: updatedProduktlinien };
    }
    case VwgActions.PRODUKT_VORBELEGT: {
      const vwgVorbelegungdaten: VwgVorbelegungdaten = action.payload;
      const vorvertragBausteinList: VorvertragBaustein<TaasTarifrisikoschluessel>[] =
        vwgVorbelegungdaten?.vorvertragBausteinList;
      const newProduktLinien = newSectionState?.produktlinien?.map(produktlinie => {
        if (produktlinie.zusatzoptionen) {
          produktlinie.zusatzoptionen.forEach(zusatzoption => {
            if (vorvertragBausteinList) {
              const vorbelegteZusatzOptionFound = vorvertragBausteinList.find(
                element => element?.taasTarifrisikoschluessel === zusatzoption?.name,
              );
              if (vorbelegteZusatzOptionFound) {
                zusatzoption.isSelected = true;
                zusatzoption.hinweis = vorbelegteZusatzOptionFound.hinweis || null;
              }
              return zusatzoption;
            }
          });
        }
        return produktlinie;
      });
      return { ...newSectionState, produktlinien: newProduktLinien };
    }
    case VwgActions.DISABLE_ELEMENTAR_ZUSATZOPTION: {
      const newProduktLinien = newSectionState.produktlinien.map(produktlinie => {
        produktlinie.zusatzoptionen = produktlinie.zusatzoptionen.map(zusatzoption => {
          if (zusatzoption.name === VorschadenArt.ELEMENTAR) {
            return { ...zusatzoption, disabled: true };
          }
          return zusatzoption;
        });
        return produktlinie;
      });
      return { ...newSectionState, produktlinien: newProduktLinien };
    }
    case ZUSATZOPTION_CHECKED: {
      if (action.payload['name'] == ZusatzoptionenNameVWGEnum.ELEMENTAR) {
        const newProduktLinien = newSectionState.produktlinien.map(produktlinie => {
          produktlinie.zusatzoptionen = produktlinie.zusatzoptionen.map(zusatzoption => {
            if (zusatzoption.name === VorschadenArt.ELEMENTAR) {
              return { ...zusatzoption, isSelected: true, disabled: false };
            }
            return zusatzoption;
          });
          return produktlinie;
        });
        return { ...newSectionState, produktlinien: newProduktLinien };
      }
      return newSectionState;
    }
    case VwgActions.VERSICHERTE_NEBENGEBAEUDE_LEISTUNG_CHANGED: {
      const { abgewaehlt } = action.payload;
      const newProduktlinien = newSectionState.produktlinien.map(produktlinie => ({
        ...produktlinie,
        leistungen: produktlinie.leistungen.map(leistung =>
          leistung.id === 'versicherteNebengebaeude'
            ? { ...leistung, abgewaehlt }
            : leistung
        )
      }));
      return { ...newSectionState, produktlinien: newProduktlinien };
    }
    case VwgActions.HAUS_TECHNIK_SCHUTZ_LEISTUNG_CHANGED: {
      const { produktlinieName, abgewaehlt } = action.payload;
      return updateLeistungAbgewaehlt(newSectionState, produktlinieName, 'hausTechnikSchutz', abgewaehlt);
    }
    case GlobalActions.PRODUKT_RECEIVED_AFTER_SAVE: {
      const actionPayload: MasterProduktData = action.payload;
      if (actionPayload.appId !== App.VWG_V2 || actionPayload.angebotsId !== state?.angebotsId) {
        return newSectionState;
      }
      return {
        ...newSectionState,
        produktlinien: refreshAussenpoolFachschluessel(
          newSectionState.produktlinien,
          actionPayload.produktData.produktlinien,
        ),
      };
    }
    default: {
      return newSectionState;
    }
  }

  function updateLeistungAbgewaehlt(
    sectionState: Produkt,
    produktlinieName: string,
    leistungId: string,
    abgewaehlt: boolean
  ): Produkt {
    return {
      ...sectionState,
      produktlinien: sectionState.produktlinien.map(produktlinie =>
        produktlinie.produktName === produktlinieName
          ? {
              ...produktlinie,
              leistungen: produktlinie.leistungen.map(leistung =>
                leistung.id === leistungId
                  ? { ...leistung, abgewaehlt }
                  : leistung
              )
            }
          : produktlinie
      )
    };
  }
};

function getVersicherteGefahrenCommaSeperated(produktlinie: Produktlinie): string {
  let gefahrenString = '';
  const gefahren: Gefahr[] = produktlinie.gefahren.filter(elem => elem.isSelected);

  for (let _i = 0; _i < gefahren.length; _i++) {
    gefahrenString += gefahren[_i].label;
    if (_i < gefahren.length - 1) {
      gefahrenString += ', ';
    }
  }
  return gefahrenString;
}

function changeLeistungenBeiGefahr(leistungen: Leistung[], gefahr: Gefahr): void {
  leistungen
    .filter(lst => lst.gefahrId === gefahr.id)
    .forEach(lst => (lst.abgewaehlt = !gefahr.isSelected));
}

export function refreshAussenpoolFachschluessel(
  produktlinien: Produktlinie[],
  masterProduktlinien: Produktlinie[],
): Produktlinie[] {
  const masterAussenpool = masterProduktlinien
    ?.find(produktlinie => produktlinie.produktName === Produktlinienname.KOMFORT)
    ?.leistungen.find(leistung => leistung.id === 'aussenpool');
  if (!masterAussenpool) {
    return produktlinien;
  }
  return produktlinien.map(produktlinie =>
    produktlinie.produktName !== Produktlinienname.KOMFORT
      ? produktlinie
      : {
          ...produktlinie,
          leistungen: produktlinie.leistungen.map(leistung =>
            leistung.id === 'aussenpool'
              ? {
                  ...leistung,
                  fachschluessel: masterAussenpool.fachschluessel,
                  writeToEinreichenXML: masterAussenpool.writeToEinreichenXML,
                }
              : leistung,
          ),
        },
  );
}

function applyOeltankDisableLogic(produkt: Produkt, state: IAppState): Produkt {
  if (!produkt?.produktlinien || !state?.buildingFeatures) {
    return produkt;
  }

  const oilHeatingSelected = state.buildingFeatures.oilHeating;
  const shouldDisableOeltank = !oilHeatingSelected;

  const updatedProduktlinien = produkt.produktlinien.map(produktlinie => {
    if (!produktlinie.zusatzoptionen) {
      return produktlinie;
    }

    const updatedZusatzoptionen = produktlinie.zusatzoptionen.map(zusatzoption => {
      if (zusatzoption.name === TaasTarifrisikoschluesselVWGV2.OEL_TANK) {
        return {
          ...zusatzoption,
          disabled: shouldDisableOeltank,
          isSelected: shouldDisableOeltank ? false : zusatzoption.isSelected,
        };
      }
      return zusatzoption;
    });

    return { ...produktlinie, zusatzoptionen: updatedZusatzoptionen };
  });

  return { ...produkt, produktlinien: updatedProduktlinien };
}
